import useInterceptor from '@jobseeker/common-core/hooks/useInterceptor';
import {handleTracking} from '@jobseeker/common-core/utils/analytics';
import {
  Bengaluru,
  Chennai,
  DelhiNCR,
  Dot,
  Hyderabad,
  Kolkata,
  Mumbai,
  Pune,
} from '@jobseeker/lotus/assets/icons';
import {useIsLargeDevice, useWrappedTheme} from '@jobseeker/lotus/hooks';
import {Box, Grid, Typography} from '@mui/material';
import {Image} from '@jobseeker/lotus/components/Image';
import {useEffect} from 'react';
import {useSelector} from 'react-redux';

import {getInheritCatOrTagSlug} from '@jobseeker/common-core/utils';
import {TAG} from '@jobseeker/common-core/constants';
import type {AppState} from '@jobseeker/common-core/types';

const SeoInterlinking = () => {
  const {
    jobfeed: {seoCityList},
    page,
    header: {
      meta: {tabsList},
    },
  }: /* eslint-disable @typescript-eslint/no-explicit-any */ any = useInterceptor();

  if (!seoCityList?.enabled) {
    return <></>;
  }

  const {slug: title, location} = useSelector(
    (state: AppState) => state.app.context.navigation
  );

  const theme = useWrappedTheme();
  const isLarge = useIsLargeDevice();

  const ICON_MAPPING: {[key: string]: string} = {
    mumbai: Mumbai,
    bangalore: Bengaluru,
    'delhi-ncr': DelhiNCR,
    hyderabad: Hyderabad,
    chennai: Chennai,
    pune: Pune,
    kolkata: Kolkata,
  };

  const cityList = location
    ? seoCityList?.content.filter((item: any) => item.label !== location)
    : seoCityList?.content?.slice(0, 6);

  const titleToShow = title?.includes('Jobs') ? title : `${title} Jobs`;

  useEffect(() => {
    handleTracking('seo_snippet_view', {
      event_category: 'Seo_snippet',
      Source: page,
      Type: 'view',
      UserId: sessionStorage.getItem('userId') || 'NA',
      Device: sessionStorage.getItem('device') || 'desktop',
      Device_Type: sessionStorage.getItem('deviceType') || 'web',
      Status: sessionStorage.getItem('status') || 'LoggedOut',
    });
  }, []);

  let {slug} = getInheritCatOrTagSlug(
    tabsList,
    title,
    page == TAG ? false : true
  ) || {
    slug: '',
  };

  const getInterlinkingLink = (key: string) => {
    let title = titleToShow;
    if (titleToShow === 'Consulting Jobs') {
      title = 'Consulting general mgmt Jobs';
      slug = 'Consulting general mgmt Jobs';
    }

    let filteredLink = `${slug
      .replace(/[^a-zA-Z0-9]/g, '-')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .toLowerCase()}-in-${key?.toLowerCase()}`;

    if (filteredLink.includes(`-jobs-in-${key?.toLowerCase()}`)) {
      return `/${filteredLink}`;
    }

    filteredLink = `${
      filteredLink.replace('-jobs-in-', '-').split(`-in-${key}`)[0]
    }-jobs-in-${key?.toLowerCase()}`;

    return `/${filteredLink}`;
  };

  if (!title) {
    return <></>;
  }

  return (
    <Box
      py={2.25}
      px={3.25}
      sx={{
        background:
          'linear-gradient(0deg, rgba(255, 255, 255, 0.70) 0%, rgba(255, 255, 255, 0.70) 100%), #E1E4E9',
      }}
      data-testid="jobfeed_major_cities_banner"
    >
      <Typography
        component="h2"
        variant="subtitle2"
      >{`${titleToShow} in major cities`}</Typography>
      <Grid
        container
        columnSpacing={3.5}
        rowSpacing={{xxs: 0.5, md: 2}}
        mt={{xxs: 0.5, md: 1.5}}
        justifyContent={'space-between'}
        sx={{
          'overflow-x': 'scroll',
          '-ms-overflow-style': 'none',
          'scrollbar-width': 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
        display={'flex'}
      >
        {cityList?.map((item: any, index: number) => (
          <Grid
            item
            xxs={12}
            md={4}
            key={`seo_list_${item.label}_${index}`}
            display={'flex'}
            alignItems={'center'}
          >
            {isLarge && (
              <Box
                sx={{
                  borderRadius: theme.spacing(1.5),
                  border: '0.6px solid #7E7E7E33',
                  p: theme.spacing(1),
                  height: 'min-content',
                  lineHeight: 0,
                  backgroundColor: 'white',
                }}
              >
                <Image
                  src={ICON_MAPPING[item.key]}
                  key={`seo_city_${item.label}_${index}}`}
                  alt={'Logo'}
                  width="30"
                  height={'30'}
                  objectFit="cover"
                  quality={50}
                />
              </Box>
            )}
            {!isLarge && <Image src={Dot} alt={'check'} width={8} height={8} />}
            <Typography
              component="a"
              href={getInterlinkingLink(item?.key)}
              target="_blank"
              variant="subtitle3"
              ml={1.75}
              sx={{
                textDecoration: 'none',
                color: theme.palette.secondary.main,
              }}
              onClick={() => {
                handleTracking('seo_snippet_click', {
                  event_category: 'Seo_snippet',
                  Source: page,
                  Type: 'click',
                  UserId: sessionStorage.getItem('userId') || 'NA',
                  Device: sessionStorage.getItem('device') || 'desktop',
                  Device_Type: sessionStorage.getItem('deviceType') || 'web',
                  Ref: `${titleToShow} in ${item.label}`,
                  Status: sessionStorage.getItem('status') || 'LoggedOut',
                });
              }}
            >
              {`${titleToShow} in ${item.label}`}
            </Typography>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default SeoInterlinking;
